import React from "react";
import { Box, Flex, Heading, Text, HStack, Icon, IconButton, Tooltip } from "@chakra-ui/react";
import { ViewIcon, DownloadIcon } from "@chakra-ui/icons";
import { COLORS } from "../styles/colors";

export const getPdfViewUrl = (item) => {
  if (item?.pdf_url) return item.pdf_url;
  if (item?.pdf_file_id) return `https://drive.google.com/file/d/${item.pdf_file_id}/preview`;
  return null;
};

export const getPdfDownloadUrl = (item) => {
  if (item?.pdf_file_id) return `https://drive.google.com/uc?export=download&id=${item.pdf_file_id}`;
  if (item?.pdf_url) return item.pdf_url;
  return null;
};

export const downloadPdf = (url, filename = "document.pdf") => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const PAID_BADGE_PROPS = {
  bg: "green.500",
  color: "white",
  fontSize: "2xs",
  px: 2.5,
  py: 0.5,
  borderRadius: "full",
  textTransform: "none",
};

export const getPaymentStatusBadgeProps = (scheme) => {
  if (scheme === "green") return PAID_BADGE_PROPS;
  return {
    colorScheme: scheme,
    variant: "subtle",
    fontSize: "2xs",
    px: 2.5,
    py: 0.5,
    borderRadius: "full",
    textTransform: "none",
  };
};

export const getInvoiceStatusBadgeProps = (isPaid) => {
  if (isPaid) return PAID_BADGE_PROPS;
  return {
    colorScheme: "orange",
    variant: "subtle",
    fontSize: "2xs",
    px: 2.5,
    py: 0.5,
    borderRadius: "full",
    textTransform: "none",
  };
};

export const PdfActionIcons = ({
  item,
  onView,
  viewLabel = "View PDF",
  downloadLabel = "Download PDF",
  getDownloadFilename,
  justify = "flex-start",
}) => {
  const viewUrl = getPdfViewUrl(item);
  const downloadUrl = getPdfDownloadUrl(item);
  const hasView = !!viewUrl;
  const hasDownload = !!downloadUrl;

  if (!hasView && !hasDownload) {
    return <Text fontSize="xs" color="gray.400">—</Text>;
  }

  const handleView = (e) => {
    e.stopPropagation();
    if (!hasView) return;
    if (onView) onView(item);
    else window.open(viewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!hasDownload) return;
    downloadPdf(downloadUrl, getDownloadFilename?.(item) || "document.pdf");
  };

  return (
    <HStack spacing={1} justify={justify} w="100%">
      <Tooltip label={viewLabel} placement="top" isDisabled={!hasView}>
        <IconButton
          icon={<ViewIcon />}
          size="sm"
          variant="ghost"
          colorScheme="teal"
          aria-label={viewLabel}
          isDisabled={!hasView}
          onClick={handleView}
        />
      </Tooltip>
      <Tooltip label={downloadLabel} placement="top" isDisabled={!hasDownload}>
        <IconButton
          icon={<DownloadIcon />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          aria-label={downloadLabel}
          isDisabled={!hasDownload}
          onClick={handleDownload}
        />
      </Tooltip>
    </HStack>
  );
};

/** Shared page title used across student portal tabs */
export const PageHeader = ({ title, subtitle, icon, iconColor = COLORS.shadowBlue, rightElement = null }) => (
  <Flex align="center" justify="space-between" gap={3} mb={subtitle ? { base: 4, md: 5 } : { base: 1, md: 1.5 }}>
    <HStack spacing={2.5} align="flex-start" minW={0}>
      {icon && (
        <Icon as={icon} color={iconColor} boxSize={5} mt={0.5} flexShrink={0} />
      )}
      <Box minW={0}>
        <Heading
          as="h1"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="700"
          color={COLORS.maastrichtBlue}
          letterSpacing="-0.02em"
          lineHeight="1.15"
          mb={0}
        >
          {title}
        </Heading>
        {subtitle && (
          <Text
            mt={1}
            fontSize={{ base: "xs", md: "sm" }}
            color="gray.500"
            fontWeight="500"
            lineHeight="short"
          >
            {subtitle}
          </Text>
        )}
      </Box>
    </HStack>
    {rightElement}
  </Flex>
);

/** Match progress dots: completed=green, ongoing=blue, upcoming/other=grey */
export const getStatusBadgeColors = (label) => {
  if (label === "Completed") return { bg: "green.500", color: "white" };
  if (label === "Ongoing") return { bg: "blue.500", color: "white" };
  return { bg: "gray.200", color: "gray.700" };
};

export const sectionCardProps = {
  p: { base: 4, md: 5 },
  bg: "white",
  borderRadius: "xl",
  border: "1px solid",
  borderColor: "gray.200",
};
